import { InstalledIntegrationType } from "./installedintegration";
import { ParagonConfiguration, ParagonCredential } from "./paragon";

export enum ParagonPipeline {
  CONTACTS = "contacts",
  COMPANIES = "companies",
  CUSTOM_OBJECTS = "custom_objects",
  DEALS = "deals",
  FILES = "files",
}

export enum ParagonSyncStatus {
  INITIALIZING = "INITIALIZING",
  ACTIVE = "ACTIVE",
  IDLE = "IDLE",
  DISABLED = "DISABLED",
  ERRORED = "ERRORED",
}

export interface ParagonSyncRecord {
  id: string;
  pipeline: ParagonPipeline;
  status: ParagonSyncStatus;
  lastSyncAt: string | null;
  errorMessage: string | null;
  errorType: string | null;
}

export interface ParagonConfig {
  id: string;
  firmId: string;
  connectionId: string;
  configuration: {
    allConfigurations: ParagonConfiguration[];
    allCredentials: ParagonCredential[];
    sharedSettings?: ParagonSharedSettings;
    [key: string]: any;
  };
  integration: InstalledIntegrationType;
  syncs?: ParagonSyncRecord[];
}

/**
 * UUID keys for Paragon shared settings.
 * These correspond to the backend ParagonConfigSettingsEnum.
 */
export enum ParagonConfigDynamicsSettingsEnum {
  ENABLE_CONTACT_SYNC = "f0e80ee6-03fe-4b07-9892-265c78b353b7",
  PUSH_SUMMARIES_TO_CONTACTS = "30d692e5-59bc-47d9-9037-bc5e8f91724a",
  PUSH_SUMMARIES_TO_ACCOUNTS = "6f7c653a-a578-4292-ac9f-d6da856f33fc",
  PUSH_TASKS_TO_CONTACTS = "963e7dad-7dea-4bd2-af07-21e54f3ffbbb",
  PUSH_TASKS_TO_ACCOUNTS = "1832abea-3ba8-40cb-9051-bc738cf87bde",
}

export enum ParagonConfigZohoSettingsEnum {
  ENABLE_CONTACT_SYNC = "c839d9ba-dbd0-4d67-a156-373638a79656",
  PUSH_SUMMARIES_TO_CONTACTS = "e4be5347-f836-47ca-abb8-172a4c9789f6",
  PUSH_SUMMARIES_TO_ACCOUNTS = "6dbc0f4b-db35-4ab0-b235-b417d51bcc6e",
  PUSH_TASKS_TO_CONTACTS = "26769a59-7dc7-4d07-a2ea-a4b69c4d9352",
  PUSH_TASKS_TO_ACCOUNTS = "8373152f-5420-43f1-9360-9cc675aa90ce",
}

/**
 * Cloven uses the same Paragon integration as Zoho ("zohocrm"), so it shares
 * the same workflow setting UUIDs. The only difference is the 5-minute
 * incremental accumulator frequency applied at sync creation time.
 */
export enum ParagonConfigClovenSettingsEnum {
  ENABLE_CONTACT_SYNC = "c839d9ba-dbd0-4d67-a156-373638a79656",
  PUSH_SUMMARIES_TO_CONTACTS = "e4be5347-f836-47ca-abb8-172a4c9789f6",
  PUSH_SUMMARIES_TO_ACCOUNTS = "6dbc0f4b-db35-4ab0-b235-b417d51bcc6e",
  PUSH_TASKS_TO_CONTACTS = "26769a59-7dc7-4d07-a2ea-a4b69c4d9352",
  PUSH_TASKS_TO_ACCOUNTS = "8373152f-5420-43f1-9360-9cc675aa90ce",
}

/**
 * Salesforce Paragon sharedSettings uses human-readable keys.
 */
export enum ParagonSalesforceConfigSettingsEnum {
  SUMMARY_OBJECT = "summaryObject",
  CONTACT_SYNC = "contactSync",
  CONTACT_SYNC_OBJECT_API_NAME = "contactSyncObjectApiName",
  CONTACT_SYNC_RECORD_TYPE_ID = "contactSyncRecordTypeId",
  PUSH_TARGET_OBJECTS = "pushTargetObjects",
}

/**
 * Configuration for which Salesforce object types advisors can attach summaries to.
 */
export interface SalesforcePushTargetObjectsConfig {
  households: boolean;
  accounts: boolean;
  personAccounts: boolean;
  contacts: boolean;
  leads: boolean;
  customObjects: string[];
}

/**
 * Paragon shared settings interface.
 * Settings are keyed by UUIDs from ParagonConfigSettingsEnum.
 */
export interface ParagonSharedSettings {
  // Dynamics settings
  [ParagonConfigDynamicsSettingsEnum.PUSH_SUMMARIES_TO_CONTACTS]?: boolean;
  [ParagonConfigDynamicsSettingsEnum.PUSH_SUMMARIES_TO_ACCOUNTS]?: boolean;
  [ParagonConfigDynamicsSettingsEnum.PUSH_TASKS_TO_CONTACTS]?: boolean;
  [ParagonConfigDynamicsSettingsEnum.PUSH_TASKS_TO_ACCOUNTS]?: boolean;
  [ParagonConfigDynamicsSettingsEnum.ENABLE_CONTACT_SYNC]?: boolean;
  [ParagonConfigZohoSettingsEnum.ENABLE_CONTACT_SYNC]?: boolean;
  [ParagonConfigZohoSettingsEnum.PUSH_SUMMARIES_TO_CONTACTS]?: boolean;
  [ParagonConfigZohoSettingsEnum.PUSH_SUMMARIES_TO_ACCOUNTS]?: boolean;
  [ParagonConfigZohoSettingsEnum.PUSH_TASKS_TO_CONTACTS]?: boolean;
  [ParagonConfigZohoSettingsEnum.PUSH_TASKS_TO_ACCOUNTS]?: boolean;
  // Salesforce settings
  [ParagonSalesforceConfigSettingsEnum.CONTACT_SYNC]?: boolean;
  [ParagonSalesforceConfigSettingsEnum.PUSH_TARGET_OBJECTS]?: SalesforcePushTargetObjectsConfig;
  // Cloven settings (same UUIDs as Zoho — Cloven IS zohocrm under the hood)
  [ParagonConfigClovenSettingsEnum.ENABLE_CONTACT_SYNC]?: boolean;
  [ParagonConfigClovenSettingsEnum.PUSH_SUMMARIES_TO_CONTACTS]?: boolean;
  [ParagonConfigClovenSettingsEnum.PUSH_SUMMARIES_TO_ACCOUNTS]?: boolean;
  [ParagonConfigClovenSettingsEnum.PUSH_TASKS_TO_CONTACTS]?: boolean;
  [ParagonConfigClovenSettingsEnum.PUSH_TASKS_TO_ACCOUNTS]?: boolean;
}

/**
 * Parsed push settings with readable property names.
 */
export interface ParagonPushSettings {
  summariesToContacts: boolean;
  summariesToAccounts: boolean;
  tasksToContacts: boolean;
  tasksToAccounts: boolean;
  /**
   * Salesforce-specific push target objects configuration.
   * Only present for Salesforce integrations.
   */
  pushTargetObjects?: SalesforcePushTargetObjectsConfig;
}
